/**
 * In-app electronic signature of a BSD (FR-FIA-19; Technical Specifications 8.5, level 1): an
 * authenticated user signs the issue, the pickup or the reception of a bordereau with a one-time
 * code, the signature is drawn on the PDF and its evidence is kept. The rules here are pure: the
 * API loads the facts, sends the codes and stores the evidence.
 */
import { z } from 'zod';
/** How a site signs its bordereaux: scanned paper copies (FR-FIA-18) or in-app (FR-FIA-19). */
export const SIGNATURE_MODES = ['PAPER', 'IN_APP'];
export const SIGNATURE_STEPS = ['ISSUE', 'PICKUP', 'RECEPTION'];
export const SIGNATURE_PARTIES = ['PRODUCER', 'TRANSPORTER', 'DESTINATION'];
/** The party each step is signed for (décret 2-14-85 art. 15, 19 and 20). */
export const PARTY_OF_STEP = {
    ISSUE: 'PRODUCER',
    PICKUP: 'TRANSPORTER',
    RECEPTION: 'DESTINATION',
};
/** Minutes a one-time code stays valid, and the wrong entries it survives. */
export const CODE_VALIDITY_MINUTES = 10;
export const CODE_MAX_ATTEMPTS = 5;
/** Challenges one user may open within the code validity, against mail flooding. */
export const CODE_MAX_CHALLENGES = 10;
const uuid = z.string().uuid();
export const signatureChallengeSchema = z
    .object({
    step: z.enum(SIGNATURE_STEPS),
    /** The bordereaux to sign together, or every eligible one of a chargement. */
    expeditionIds: z.array(uuid).min(1).max(50).optional(),
    chargementId: uuid.optional(),
    /** The person who signs for the transporter or the destination; the user for the producer. */
    signerName: z.string().trim().min(2).max(120).optional(),
})
    .refine((v) => Boolean(v.expeditionIds) !== Boolean(v.chargementId), {
    message: 'Give either expeditionIds or chargementId',
    path: ['expeditionIds'],
});
export const signatureSignSchema = z.object({
    code: z.string().regex(/^\d{6}$/, 'A code is six digits'),
    /** The explicit consent to the text shown (Technical Specifications 8.5). */
    consent: z.literal(true),
});
export const signatureWithdrawSchema = z.object({
    reason: z.string().trim().min(3).max(500),
});
/** Statuses from which each step may be signed: the data of the step is recorded and frozen. */
const SIGNABLE = {
    ISSUE: ['AWAITING_SHIPMENT'],
    PICKUP: ['SHIPPED', 'TEMP_STORED', 'FORWARDED', 'RECEIVED', 'ACCEPTED', 'REFUSED', 'TREATED'],
    RECEPTION: ['RECEIVED', 'ACCEPTED', 'REFUSED', 'TREATED'],
};
/**
 * Whether a step can be signed on an expédition: once its data is recorded, once only, and
 * never on a cancelled bordereau. The issue is signed before the pickup, which freezes the BSD.
 */
export function stepAvailability(step, status, active) {
    if (status === 'CANCELLED')
        return { available: false, reason: 'CANCELLED' };
    if (active.some((s) => s.step === step))
        return { available: false, reason: 'ALREADY_SIGNED' };
    if (SIGNABLE[step].includes(status))
        return { available: true };
    if (step === 'ISSUE') {
        return {
            available: false,
            reason: status === 'DRAFT' || status === 'READY' ? 'NOT_YET' : 'TOO_LATE',
        };
    }
    return { available: false, reason: 'NOT_YET' };
}
/** The e-mail with most of its local part hidden, for the screen and the evidence. */
export function maskEmail(email) {
    const at = email.indexOf('@');
    if (at <= 0)
        return email;
    const local = email.slice(0, at);
    const shown = local.length <= 2 ? local.slice(0, 1) : local.slice(0, 2);
    return `${shown}${'*'.repeat(Math.max(2, local.length - shown.length))}${email.slice(at)}`;
}
const STEP_TEXT = {
    ISSUE: "certifie l'exactitude des mentions du bordereau et la remise du déchet au transporteur désigné",
    PICKUP: 'certifie la prise en charge du déchet à la date, dans la quantité et avec le véhicule mentionnés sur le bordereau',
    RECEPTION: 'certifie la réception du déchet à la date et dans la quantité mentionnées sur le bordereau, avec la décision indiquée',
};
const PARTY_TEXT = {
    PRODUCER: "pour le compte de l'expéditeur",
    TRANSPORTER: 'pour le compte du transporteur',
    DESTINATION: 'pour le compte du destinataire',
};
/** The consent text signed, in French as the bordereau (loi 43-20, signature électronique simple). */
export function consentText(input) {
    const party = PARTY_OF_STEP[input.step];
    const bsd = input.bsdNumbers.length === 1
        ? `du bordereau de suivi n° ${input.bsdNumbers[0]}`
        : `des bordereaux de suivi n° ${input.bsdNumbers.join(', ')}`;
    return (`Je soussigné(e) ${input.signerName}, ${PARTY_TEXT[party]} (${input.siteName}), ` +
        `${STEP_TEXT[input.step]} ${bsd}. ` +
        "J'accepte que cette signature électronique, confirmée par le code à usage unique reçu, " +
        'soit apposée sur le document et conservée avec ses preuves (identité, date, adresse réseau, empreinte du document), ' +
        'conformément à la loi 43-20 relative aux services de confiance pour les transactions électroniques.');
}
//# sourceMappingURL=signature.js.map